// src/components/AIChat.js
import { useState, useRef, useEffect } from "react";

import { GROQ_API_KEY, GROQ_MODEL } from "../config";

async function callGroq(messages) {
  const resp = await fetch("https://api.groq.com/openai/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${GROQ_API_KEY}`,
    },
    body: JSON.stringify({
      model: GROQ_MODEL,
      max_tokens: 600,
      temperature: 0.7,
      messages,
    }),
  });
  const data = await resp.json();
  if (data.error) throw new Error(data.error.message);
  return data.choices?.[0]?.message?.content || "";
}

export default function AIChat({ task, onSaveChat }) {
  const [messages, setMessages] = useState(task.chatHistory || []);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const bottomRef = useRef(null);

  // Scroll to bottom when new message arrives
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Build system prompt with full task context
  function buildSystemPrompt() {
    return `You are a helpful home repair expert assistant. You are helping a homeowner with the following repair task:

Task: ${task.title}
Category: ${task.category}
Priority: ${task.priority}
${task.notes ? `Description: ${task.notes}` : ""}
${task.repairGuide ? `\nExisting Repair Guide:\n${task.repairGuide}` : ""}

Answer questions helpfully, concisely and practically. Stay focused on this specific repair task. If asked about something unrelated to home repair, politely redirect. Always prioritize safety.`;
  }

  async function handleSend(e) {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = { role: "user", content: input.trim(), timestamp: new Date().toISOString() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput("");
    setLoading(true);
    setError("");

    try {
      // Build message history for Groq (without timestamps, just role/content)
      const groqMessages = [
        { role: "system", content: buildSystemPrompt() },
        ...updatedMessages.map(m => ({ role: m.role, content: m.content })),
      ];

      const reply = await callGroq(groqMessages);
      const assistantMessage = { role: "assistant", content: reply, timestamp: new Date().toISOString() };
      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);

      // Save to Firestore so household members can see it
      onSaveChat(finalMessages);
    } catch (err) {
      setError("Failed to get a response. Please try again.");
      console.error("Chat error:", err);
    } finally {
      setLoading(false);
    }
  }

  function handleClearChat() {
    if (!window.confirm("Clear the entire chat history for this task?")) return;
    setMessages([]);
    onSaveChat([]);
  }

  const suggestedQuestions = [
    "What tools do I need?",
    "How long will this take?",
    "Is this safe to DIY?",
    "What could go wrong?",
    "Do I need a permit?",
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, color: "#1e3a5f" }}>AI Repair Assistant</div>
          <div style={{ fontSize: 11, color: "#6b88b8", fontWeight: 600, marginTop: 2 }}>
            Ask anything about this repair — answers are saved for your household
          </div>
        </div>
        {messages.length > 0 && (
          <button onClick={handleClearChat} style={styles.clearBtn}>Clear chat</button>
        )}
      </div>

      {/* Messages */}
      <div style={styles.messagesContainer}>
        {messages.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>💬</div>
            <p style={{ fontWeight: 700, color: "#6b88b8", marginBottom: 16, fontSize: 14 }}>
              Ask me anything about this repair!
            </p>
            {/* Suggested questions */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
              {suggestedQuestions.map(q => (
                <button
                  key={q}
                  onClick={() => setInput(q)}
                  style={styles.suggestionBtn}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => (
              <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", marginBottom: 12 }}>
                {msg.role === "assistant" && (
                  <div style={styles.aiAvatar}>AI</div>
                )}
                <div style={{
                  ...styles.messageBubble,
                  ...(msg.role === "user" ? styles.userBubble : styles.aiBubble),
                }}>
                  <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{msg.content}</div>
                  <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, textAlign: msg.role === "user" ? "right" : "left" }}>
                    {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={styles.aiAvatar}>AI</div>
                <div style={{ ...styles.messageBubble, ...styles.aiBubble }}>
                  <div style={styles.typingDots}>
                    <span /><span /><span />
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </>
        )}
      </div>

      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700, margin: "0 0 8px 0" }}>
          {error}
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSend} style={styles.inputRow}>
        <input
          style={styles.input}
          placeholder="Ask a question about this repair..."
          value={input}
          onChange={e => setInput(e.target.value)}
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          style={styles.sendBtn}
        >
          {loading ? "..." : "Send"}
        </button>
      </form>
    </div>
  );
}

const styles = {
  container: {
    display: "flex", flexDirection: "column", height: 480,
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    marginBottom: 12, paddingBottom: 12, borderBottom: "2px solid #dbeafe",
  },
  clearBtn: {
    background: "none", border: "none", color: "#6b88b8", cursor: "pointer",
    fontSize: 12, fontWeight: 700, fontFamily: "'Nunito', sans-serif",
    textDecoration: "underline",
  },
  messagesContainer: {
    flex: 1, overflowY: "auto", marginBottom: 12,
    paddingRight: 4,
  },
  emptyState: {
    textAlign: "center", padding: "32px 16px",
  },
  suggestionBtn: {
    background: "white", border: "2px solid #dbeafe", borderRadius: 20,
    padding: "6px 14px", cursor: "pointer", fontFamily: "'Nunito', sans-serif",
    fontSize: 12, fontWeight: 700, color: "#3b82f6", transition: "all 0.18s",
  },
  messageBubble: {
    maxWidth: "75%", padding: "10px 14px", borderRadius: 16,
    fontFamily: "'Nunito', sans-serif", fontSize: 13, fontWeight: 600,
  },
  userBubble: {
    background: "linear-gradient(135deg, #3b82f6, #6366f1)",
    color: "white", borderBottomRightRadius: 4,
  },
  aiBubble: {
    background: "white", color: "#1e3a5f",
    border: "1px solid #dbeafe", borderBottomLeftRadius: 4,
    boxShadow: "0 2px 8px rgba(59,130,246,0.08)",
  },
  aiAvatar: {
    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
    background: "linear-gradient(135deg, #3b82f6, #6366f1)",
    color: "white", display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 10, fontWeight: 900, marginRight: 8, alignSelf: "flex-end",
  },
  inputRow: {
    display: "flex", gap: 8,
  },
  input: {
    flex: 1, padding: "10px 14px", border: "2px solid #dbeafe",
    borderRadius: 12, fontFamily: "'Nunito', sans-serif", fontSize: 14,
    fontWeight: 600, color: "#1e3a5f", background: "white", outline: "none",
  },
  sendBtn: {
    background: "linear-gradient(135deg, #3b82f6, #6366f1)",
    border: "none", color: "white", padding: "10px 20px", borderRadius: 12,
    cursor: "pointer", fontFamily: "'Nunito', sans-serif", fontSize: 14,
    fontWeight: 800, transition: "all 0.18s",
  },
  typingDots: {
    display: "flex", gap: 4, alignItems: "center", padding: "4px 0",
  },
};
